import { BIG_ONE, PositionDraft, PositionSide } from "@perp/sdk-curie"
import Big from "big.js"

interface GetLiquidationPriceParams {
    side: PositionSide
    indexTwapPrice: Big
    mmRatio: Big
    accountValue: Big
    totalPositionValue: Big
}
// NOTE: currently we don't use this formula and get this from contract call instead
// NOTE: long  => Index Twap Price * ( 1 + Maintenance Margin Ratio - Account Value / Total Position Value )
// NOTE: short => Index Twap Price * ( 1 - Maintenance Margin Ratio + Account Value / Total Position Value )
export function getLiquidationPrice({
    side,
    indexTwapPrice,
    mmRatio,
    accountValue,
    totalPositionValue,
}: GetLiquidationPriceParams) {
    if (totalPositionValue.eq(0)) {
        return undefined
    }

    const isLong = side === PositionSide.LONG
    const currentMarginRatio = accountValue.div(totalPositionValue)

    return isLong
        ? indexTwapPrice.mul(mmRatio.minus(currentMarginRatio).add(1))
        : indexTwapPrice.mul(currentMarginRatio.minus(mmRatio).add(1))
}

interface GetNextLiquidationPriceParams {
    positionDraft: PositionDraft
    mmRatio: Big
    indexTwapPrice: Big
    nextAccountValue: Big
    deltaAvailableBase: Big
    nextTotalPositionValue: Big
}

// NOTE: currently we don't use this formula, might remove it later
export function getNextLiquidationPrice({
    positionDraft,
    mmRatio,
    indexTwapPrice,
    nextAccountValue,
    deltaAvailableBase,
    nextTotalPositionValue,
}: GetNextLiquidationPriceParams) {
    // NOTE: There's no liquidation price when closing position
    if (nextTotalPositionValue.eq(0)) {
        return undefined
    }

    // NOTE: there's no positionDraft.existingPosition in the latest design
    const existingPosition: any = undefined // positionDraft.existingPosition
    const isReversing = existingPosition && existingPosition.size.lt(deltaAvailableBase)
    const nextSide = !existingPosition || isReversing ? positionDraft.side : existingPosition.side

    return getLiquidationPrice({
        side: nextSide,
        indexTwapPrice,
        mmRatio,
        accountValue: nextAccountValue,
        totalPositionValue: nextTotalPositionValue,
    })
}

interface GetNextMarginRatioParams {
    nextAccountValue: Big
    nextTotalPositionValue: Big
}
// NOTE: currently we don't use this formula, might remove it later
export function getNextMarginRatio({ nextAccountValue, nextTotalPositionValue }: GetNextMarginRatioParams) {
    //NOTE: no position = infinity margin ratio
    if (nextTotalPositionValue.eq(0)) {
        return undefined
    }
    return nextAccountValue.div(nextTotalPositionValue)
}

interface GetNextLeverageParams {
    nextMarginRatio: Big
}
// NOTE: currently we don't use this formula, might remove it later
export function getNextLeverage({ nextMarginRatio }: GetNextLeverageParams) {
    //NOTE: no position = infinity margin ratio = 0 leverage
    if (nextMarginRatio.eq(0)) {
        return undefined
    }
    return BIG_ONE.div(nextMarginRatio)
}

/**
 * NOTE:
 * newTotalPositionValue = totalPositionValue + deltaTotalPositionValue
 *
 * if (increase position)
 *   deltaTotalPositionValue = abs(signedDeltaAvailableBase * indexTwapPrice)
 * if (decrease position)
 *   deltaTotalPositionValue = -1 * abs(signedDeltaAvailableBase * indexTwapPrice)
 * if (reverse position)
 *   deltaTotalPositionValue = - abs(existingPositionValue) + (abs(signedDeltaAvailableBase * indexTwapPrice) - abs(existingPositionValue))
 *							 =  abs(signedDeltaAvailableBase * indexTwapPrice) - abs(existingPositionValue) * 2
 */
interface GetNextTotalPositionValueParams {
    deltaAvailableBase: Big
    indexTwapPrice: Big
    positionDraft: PositionDraft
    totalPositionValue: Big
}
// NOTE: getNextTotalPositionValue is not using right now after we refactor PositionDraft
// and nextTotalPositionValue is used to calculate nextliquidationPrice
// we will revisit this implementation later.
export function getNextTotalPositionValue({
    deltaAvailableBase,
    indexTwapPrice,
    positionDraft,
    totalPositionValue,
}: GetNextTotalPositionValueParams) {
    // NOTE: there's no positionDraft.existingPosition in the latest design
    const existingPosition: any = undefined // positionDraft.existingPosition
    const isSameSide = !existingPosition || existingPosition.side === positionDraft.side
    const isReversing = existingPosition && existingPosition.size.lt(deltaAvailableBase)

    let deltaPositionValue = deltaAvailableBase.mul(indexTwapPrice).mul(isSameSide ? 1 : -1)
    if (isReversing) {
        deltaPositionValue = deltaPositionValue.abs().sub(existingPosition!.size.mul(indexTwapPrice).mul(2))
    }

    return totalPositionValue.add(deltaPositionValue)
}

export const strToBig = (str: string) => {
    try {
        return new Big(str)
    } catch (error) {
        return undefined
    }
}
